// frontend/src/pages/FolderBrowser.tsx - v2.8
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  VideoLibrary as AnalysisIcon,
} from '@mui/icons-material';
import { 
  fetchRootFolders, 
  fetchFolderContents,
  getPresignedViewUrl,
  Folder,
  FolderFile,
  Breadcrumb,
  FileType,
  formatFileSize,
  apiLogger
} from '../services/api';
import { getDisplayFileName, getDisplayFolderName, getFileTypeFromExtension } from '../services/FileUtils';
import { recordActivity } from '../services/activityService';
import { enqueueVideoForAnalysis } from '../services/analysisSession';

interface FolderData {
  folders: Folder[];
  files: FolderFile[];
  breadcrumbs: Breadcrumb[];
}

type Order = 'asc' | 'desc';
type SortField = 'name' | 'lastModified';

const FolderBrowser: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const foldersOnly = new URLSearchParams(location.search).get('foldersOnly') === 'true';
  
  // Derive folderPath directly from location — no intermediate state,
  // so there's no render gap between navigation and the fetch starting.
  const folderPath = location.pathname.startsWith('/folders/')
    ? decodeURIComponent(location.pathname.substring('/folders/'.length))
    : '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [folderData, setFolderData] = useState<FolderData>({
    folders: [],
    files: [],
    breadcrumbs: []
  });
  
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortField>('lastModified');
  const [visibleFolders, setVisibleFolders] = useState(25);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setFolderData({ folders: [], files: [], breadcrumbs: [] });
      setVisibleFolders(25);
      try {
        const response = folderPath
          ? await fetchFolderContents(folderPath)
          : await fetchRootFolders();

        if (cancelled) return;
        apiLogger.info('FolderBrowser Fetch', 'Raw folder data:', response);
        setFolderData(response || { folders: [], files: [], breadcrumbs: [] });
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching folder data:', err);
        setError(`Failed to load folder: ${(err as Error).message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [folderPath]);

  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedFolders = useMemo(() => {
    if (!folderData.folders) return [];
    
    return [...folderData.folders].sort((a, b) => {
      if (orderBy === 'lastModified') {
        if (!a.lastModified && !b.lastModified) return 0;
        if (!a.lastModified) return order === 'asc' ? -1 : 1;
        if (!b.lastModified) return order === 'asc' ? 1 : -1;
        
        const dateA = new Date(a.lastModified).getTime();
        const dateB = new Date(b.lastModified).getTime();
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      const comparison = a.name.localeCompare(b.name);
      return order === 'asc' ? comparison : -comparison;
    });
  }, [folderData.folders, orderBy, order]);

  const handleFolderClick = (folderPath: string) => {
    navigate(`/folders/${encodeURIComponent(folderPath)}`);
  };

  const handleFileClick = (fileKey: string) => {
    const fileType = getFileTypeFromExtension(fileKey);
    if (fileType === FileType.VIDEO) {
      recordActivity(fileKey, 'played', FileType.VIDEO);
      navigate(`/play/${encodeURIComponent(fileKey)}`);
    } else if (fileType === FileType.CSV) {
      recordActivity(fileKey, 'viewed', FileType.CSV);
      navigate(`/view/csv/${encodeURIComponent(fileKey)}`);
    } else if (fileType === FileType.TEXT) {
      recordActivity(fileKey, 'viewed', FileType.TEXT);
      navigate(`/view/text/${encodeURIComponent(fileKey)}`);
    } else if (fileType === FileType.IMAGE) {
      recordActivity(fileKey, 'viewed', FileType.IMAGE);
      navigate(`/view/image/${encodeURIComponent(fileKey)}`);
    } else {
      recordActivity(fileKey, 'played', fileType);
      navigate(`/play/${encodeURIComponent(fileKey)}`);
    }
  };

  // Fire-and-forget presigned URL pre-fetch on hover.
  // getPresignedViewUrl checks the cache first, so hovering the same row twice
  // costs nothing after the first fetch.
  const handleFileHover = (key: string) => {
    if (getFileTypeFromExtension(key) === FileType.VIDEO) {
      getPresignedViewUrl(key).catch(() => {});
    }
  };

  const handleAddToAnalysis = (key: string) => {
    enqueueVideoForAnalysis(key);
    navigate('/analysis');
  };

  const handleBreadcrumbClick = (path?: string) => {
    navigate(path ? `/folders/${encodeURIComponent(path)}` : '/folders');
  };

  const handleBackClick = () => {
    if (folderData.breadcrumbs.length > 1) {
      const parentPath = folderData.breadcrumbs[folderData.breadcrumbs.length - 2].path;
      navigate(parentPath ? `/folders/${encodeURIComponent(parentPath)}` : '/folders');
    } else {
      navigate('/folders');
    }
  };

  const getFileIcon = (filename?: string) => {
    if (!filename) {
      apiLogger.warn('FolderBrowser', 'getFileIcon received undefined filename', { folderPath });
      return <FileIcon color="primary" />;
    }
    
    const extension = filename.split('.').pop()?.toLowerCase();
    if (extension === 'mp4' || extension === 'mov' || extension === 'webm') {
      return <FileIcon color="primary" />; // Removed VideoIcon, using FileIcon as fallback
    }
    return <FileIcon color="primary" />;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString('en-US', { month: 'short' }) + ' ' + date.getDate() + 
             (new Date().getFullYear() !== date.getFullYear() ? ', ' + date.getFullYear() : '');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  const loadMoreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleFolders < folderData.folders.length) {
        setVisibleFolders(prev => Math.min(prev + 25, folderData.folders.length));
      }
    });
    if (loadMoreRef.current) observer.current.observe(loadMoreRef.current);
    return () => { if (observer.current) observer.current.disconnect(); };
  }, [folderData.folders, visibleFolders]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ my: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const title = folderPath 
    ? getDisplayFolderName(folderPath.split('/').filter(Boolean).pop() || 'Folder')
    : 'Folders';

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {folderData.breadcrumbs.length > 1 && (
          <Button 
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
        )}
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
      </Box>

      {folderData.breadcrumbs.filter(c => c.name !== 'Root').length > 0 && (
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="folder navigation breadcrumbs"
          sx={{ mb: 3 }}
        >
          {folderData.breadcrumbs.filter(c => c.name !== 'Root').map((crumb, index, arr) => {
            const isLast = index === arr.length - 1;
            return isLast ? (
              <Typography key={crumb.path || 'root'} color="text.primary" fontWeight={500}>
                {getDisplayFolderName(crumb.name)}
              </Typography>
            ) : (
              <MuiLink
                key={crumb.path || 'root'}
                component="button"
                variant="body1"
                onClick={() => handleBreadcrumbClick(crumb.path)}
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
                color="primary"
              >
                {getDisplayFolderName(crumb.name)}
              </MuiLink>
            );
          })}
        </Breadcrumbs>
      )}

      {sortedFolders.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Folders
          </Typography>
          <TableContainer component={Paper}>
            <Table aria-label="folder list">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell 
                    sortDirection={orderBy === 'name' ? order : false}
                    sx={{ fontWeight: 'bold', minWidth: '400px' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleRequestSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    sortDirection={orderBy === 'lastModified' ? order : false}
                    sx={{ fontWeight: 'bold', minWidth: '150px' }}
                    align="right"
                  >
                    <TableSortLabel
                      active={orderBy === 'lastModified'}
                      direction={orderBy === 'lastModified' ? order : 'asc'}
                      onClick={() => handleRequestSort('lastModified')}
                    >
                      Last Modified
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFolders.slice(0, visibleFolders).map((folder) => (
                  <TableRow
                    key={folder.path}
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                    onClick={() => handleFolderClick(folder.path)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FolderIcon color="primary" sx={{ mr: 1 }} />
                        <Typography>
                          {getDisplayFolderName(folder.name)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatDate(folder.lastModified)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {visibleFolders < sortedFolders.length && (
            <Box ref={loadMoreRef} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" onClick={() => setVisibleFolders(prev => prev + 25)}>Load More</Button>
            </Box>
          )}
        </Box>
      )}

      {!foldersOnly && folderData.files.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Files
          </Typography>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="file list">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: '400px' }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: '150px' }} align="right">
                    Last Modified
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: '100px' }} align="right">
                    File Size
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '140px' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {folderData.files.map((file) => (
                  <TableRow
                    key={file.key}
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                    onClick={() => handleFileClick(file.key)}
                    onMouseEnter={() => handleFileHover(file.key)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getFileIcon(file.name)}
                        <Typography sx={{ ml: 2, color: 'text.primary' }}>
                          {getDisplayFileName(file.key) || 'Unnamed File'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatDate(file.lastModified)}
                    </TableCell>
                    <TableCell align="right">
                      {file.size ? formatFileSize(file.size) : 'N/A'}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {getFileTypeFromExtension(file.key) === FileType.VIDEO && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AnalysisIcon fontSize="small" />}
                          onClick={() => handleAddToAnalysis(file.key)}
                          sx={{ fontSize: '0.7rem', py: 0.25, px: 1, whiteSpace: 'nowrap' }}
                        >
                          Analyze
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {folderData.folders.length === 0 && folderData.files.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            This folder is empty
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default FolderBrowser;